import { LatLngBounds, tooltip as LeafletTooltip, LatLng } from 'leaflet'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useMap, useMapEvents } from 'react-leaflet'
import { ShapeType, ShapeProps } from '@kouto/types'
import { getContrastYIQ } from 'dashboard/utils/colors'

type ShapeRefType<T extends ShapeType> = T extends 'circle'
  ? L.Circle
  : L.Rectangle

type HookResponse<T extends ShapeType> = {
  bounds: LatLngBounds
  shapeRef: React.MutableRefObject<ShapeRefType<T> | null>
}

export const useDraggableShape = <T extends ShapeType>(
  shape: ShapeProps,
  canDragOutsideMap: boolean,
  mapSize: { width: number; height: number },
): HookResponse<T> => {
  const map = useMap()
  const [bounds, setBounds] = useState<LatLngBounds>(
    new LatLngBounds([
      [shape.y - shape.height / 2, shape.x - shape.width / 2],
      [shape.y + shape.height / 2, shape.x + shape.width / 2],
    ]),
  )
  const shapeRef = useRef<ShapeRefType<T> | null>(null)
  const moveCountRef = useRef(0)
  const dragStartBoundsRef = useRef<LatLngBounds | undefined>(undefined)
  const dragStartEventLatLngRef = useRef<LatLng | undefined>(undefined)

  useEffect(() => {
    if (!shapeRef.current) {
      return
    }

    const el = shapeRef.current.getElement()
    el?.classList.toggle('disabled', !!shape.disabled)
  }, [shape.disabled, shapeRef])

  useEffect(() => {
    if (!shape.name) {
      return
    }
    const el = shapeRef.current?.getElement()
    el?.setAttribute('role', 'button')
    el?.setAttribute('aria-label', shape.name)
  }, [shapeRef, shape.name])

  useEffect(() => {
    if (shape.draggable) {
      return
    }
    const el = shapeRef.current?.getElement()
    if (!el) {
      return
    }
    el.setAttribute('tabindex', shape.disabled ? '-1' : '0')

    const handleKeyDown = (event: Event) => {
      const keyEvent = event as KeyboardEvent
      if (keyEvent.key !== 'Enter' && keyEvent.key !== ' ') {
        return
      }
      keyEvent.preventDefault()
      if (!shape.disabled) {
        shape.onClick?.(shape)
      }
    }
    el.addEventListener('keydown', handleKeyDown)
    return () => {
      el.removeEventListener('keydown', handleKeyDown)
    }
  }, [shape, shapeRef])

  const onMouseDown = useCallback(
    (event: L.LeafletMouseEvent) => {
      if (!shapeRef.current) return
      if (!shape.draggable) {
        if (!shape.disabled) {
          shape.onClick?.(shape)
        }
        return
      }

      map.dragging.disable()
      moveCountRef.current = 0
      dragStartBoundsRef.current = shapeRef.current.getBounds()
      dragStartEventLatLngRef.current = event.latlng
    },
    [map.dragging, shape, shapeRef],
  )

  const onMouseMove = (event: L.LeafletMouseEvent) => {
    if (!dragStartBoundsRef.current || !dragStartEventLatLngRef.current) return
    moveCountRef.current += 1
    const delta = {
      lat: dragStartEventLatLngRef.current.lat - event.latlng.lat,
      lng: dragStartEventLatLngRef.current.lng - event.latlng.lng,
    }
    const newBounds = new LatLngBounds([
      [
        dragStartBoundsRef.current.getSouthWest().lat - delta.lat,
        dragStartBoundsRef.current.getSouthWest().lng - delta.lng,
      ],
      [
        dragStartBoundsRef.current.getNorthEast().lat - delta.lat,
        dragStartBoundsRef.current.getNorthEast().lng - delta.lng,
      ],
    ])

    const newShapeCenter = newBounds.getCenter()
    if (
      !canDragOutsideMap &&
      (newShapeCenter.lat < 0 ||
        newShapeCenter.lng < 0 ||
        newShapeCenter.lat > mapSize.height ||
        newShapeCenter.lng > mapSize.width)
    ) {
      return
    }

    const tooltip = shapeRef.current?.getTooltip()
    tooltip?.setLatLng(newBounds.getCenter())
    setBounds(newBounds)
  }

  const onMouseEnd = () => {
    if (!dragStartBoundsRef.current || !dragStartEventLatLngRef.current) return

    if (moveCountRef.current === 0) {
      if (!shape.disabled) {
        shape.onClick?.(shape)
      }
    } else {
      shape.onDrag?.({
        ...shape,
        x: bounds.getCenter().lng,
        y: bounds.getCenter().lat,
      })
    }
    map.dragging.enable()
    dragStartBoundsRef.current = undefined
    dragStartEventLatLngRef.current = undefined
  }

  useMapEvents({
    mousemove: onMouseMove,
    mouseup: onMouseEnd,
    mouseout: onMouseEnd,
  })

  useEffect(() => {
    const el = shapeRef.current?.getElement()
    const tooltip = shapeRef.current?.getTooltip()
    const tooltipContent = tooltip?.getElement()?.firstElementChild

    if (shape.isSelected) {
      tooltipContent?.classList.add('selected')

      el?.classList.remove('focused')
      el?.classList.add('selected')

      shapeRef.current?.setStyle({
        fillColor: `#fff`,
        color: shape.color,
      })
    } else if (shape.isFocused) {
      tooltipContent?.classList.remove('selected')
      el?.classList.remove('selected')
      el?.classList.add('focused')
      shapeRef.current?.setStyle({
        fillColor: `color-mix(in oklab, ${shape.color}, #000 20%)`,
        color: '#fff',
      })
    } else {
      tooltipContent?.classList.remove('selected')
      el?.classList.remove('focused')
      el?.classList.remove('selected')
      shapeRef.current?.setStyle({ fillColor: shape.color, color: '#fff' })
    }
  }, [shape.color, shape.isSelected, shape.isFocused])

  useEffect(() => {
    if (shapeRef.current) {
      if (!shapeRef.current.getTooltip()) {
        const span = document.createElement('span')
        span.innerHTML = shape.label
        span.style.color = shape.isSelected
          ? '#000'
          : getContrastYIQ(shape.color)

        const tooltip = LeafletTooltip({
          permanent: true,
          direction: 'center',
          className: 'shape-tooltip',
        })
          .setContent(span)
          .setLatLng(shapeRef.current.getBounds().getCenter())
        shapeRef.current.bindTooltip(tooltip).openTooltip()
      }

      shapeRef.current.on('mousedown', onMouseDown)
      shapeRef.current.on('mouseover', () => {
        if (shape.disabled) return
        if (shape.isSelected) {
          shapeRef.current?.setStyle({
            fillColor: `color-mix(in oklab, #fff, #000 20%)`,
            color: shape.color,
          })
        } else {
          shapeRef.current?.setStyle({
            fillColor: `color-mix(in oklab, ${shape.color}, #000 20%)`,
            color: '#fff',
          })
        }
      })
      shapeRef.current.on('mouseout', () => {
        if (shape.disabled) return
        if (shape.isSelected) {
          shapeRef.current?.setStyle({
            fillColor: `#fff`,
            color: shape.color,
          })
        } else {
          shapeRef.current?.setStyle({ fillColor: shape.color, color: '#fff' })
        }
      })
    }

    const _shape = shapeRef.current
    return () => {
      if (_shape) {
        _shape.off('mousedown')
        _shape.off('mouseover')
        _shape.off('mouseout')
      }
    }
  }, [
    shape.label,
    shape.color,
    shape.disabled,
    shape.isFocused,
    shape.isSelected,
    shapeRef,
    onMouseDown,
  ])

  // The browser's own hover text, so it needs no layer of its own beside the pin's number label.
  useEffect(() => {
    const element = shapeRef.current?.getElement()
    if (!element) return
    element.querySelector('title')?.remove()
    if (!shape.hoverText) return
    const title = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'title',
    )
    title.textContent = shape.hoverText
    element.appendChild(title)
  }, [shape.hoverText, shapeRef])

  return { bounds, shapeRef }
}
